import {
  useCreateOrUpdateMultipleprintInExhibition,
  useSyncItinerantExhibitionGroup,
} from "@/api/related-fields";
import { LinkableType } from "@/components/LinksManagement";
import { FormLayoutElement } from "@/config/forms/general";
import { MultipleprintInExhibition } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import LinksDrawerLayout from "./LinksDrawerLayout";
import {
  ItinerantExhibitionPanel,
  ItinerantStopDraft,
  itinerantStopFromExhibition,
} from "./ItinerantExhibitionPanel";
import { ItinerantExhibitionPreview } from "./ItinerantExhibitionPreview";
import { ItinerantDrawerConfig } from "./itinerant-drawer-types";
import { useItinerantDrawerChrome } from "./useItinerantDrawerChrome";
import { useItinerantStopSelection } from "./useItinerantStopSelection";

const MultipleprintInExhibitionDrawer = ({
  fields,
  data,
  queryId,
  type,
  open,
  onOpenChange,
  itinerantConfig,
}: {
  fields: FormLayoutElement[] | undefined;
  data: MultipleprintInExhibition;
  queryId: string;
  type: LinkableType;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  itinerantConfig?: ItinerantDrawerConfig;
}) => {
  const schema = z.object({
    expositionsDansEstampesMultiplesDescriptionNumero: z.string(),
    expositionsDansEstampesMultiplesDescriptionExemplaireExpose: z.string(),
    exhibition: z.object({
      expositionsDescriptionNumeroExpo: z.string(),
      expositionsDescriptionResume: z.string(),
    }),
  });

  const { mutate: createOrUpdateMultipleprintInExhibition } =
    useCreateOrUpdateMultipleprintInExhibition(queryId, type);

  const { mutate: syncGroup, isPending: isSyncing } =
    useSyncItinerantExhibitionGroup(
      itinerantConfig?.parentId ?? "",
      itinerantConfig?.parentType ?? "multipleprint",
      itinerantConfig?.complexRelationKey,
    );

  const [stops, setStops] = useState<ItinerantStopDraft[]>([]);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      exhibition: data.exhibition,
      expositionsDansEstampesMultiplesDescriptionNumero:
        data.expositionsDansEstampesMultiplesDescriptionNumero,
      expositionsDansEstampesMultiplesDescriptionExemplaireExpose:
        data.expositionsDansEstampesMultiplesDescriptionExemplaireExpose,
    },
  });

  const { selectedStopId, selectStop, flushStopsForSubmit } =
    useItinerantStopSelection(itinerantConfig, stops, setStops, form, {
      catalogNumber: "expositionsDansEstampesMultiplesDescriptionNumero",
      exemplaireExpose:
        "expositionsDansEstampesMultiplesDescriptionExemplaireExpose",
    });

  const onSubmit = (formData: z.infer<typeof schema>) => {
    if (itinerantConfig) {
      const effectiveStops = flushStopsForSubmit();
      const stopsToSync =
        effectiveStops.length > 0
          ? effectiveStops
          : [
              itinerantStopFromExhibition(itinerantConfig.seedExhibition, undefined, {
                catalogNumber:
                  formData.expositionsDansEstampesMultiplesDescriptionNumero ||
                  "",
                exemplaireExpose:
                  formData.expositionsDansEstampesMultiplesDescriptionExemplaireExpose ||
                  "",
              }),
            ];
      syncGroup({
        groupId: itinerantConfig.groupId ?? undefined,
        parentType: itinerantConfig.parentType,
        parentId: itinerantConfig.parentId,
        stops: stopsToSync.map((s, index) => ({
          linkId: s.linkId,
          exhibitionId: s.exhibition.id,
          catalogNumber: s.catalogNumber,
          exemplaireExpose: s.exemplaireExpose,
          note: s.note,
          order: index,
        })),
      });
      return;
    }

    createOrUpdateMultipleprintInExhibition({
      id: data.id,
      multipleprintId: data.multipleprint.id,
      exhibitionId: data.exhibition.id,
      expositionsDansEstampesMultiplesDescriptionNumero:
        formData.expositionsDansEstampesMultiplesDescriptionNumero,
      expositionsDansEstampesMultiplesDescriptionExemplaireExpose:
        formData.expositionsDansEstampesMultiplesDescriptionExemplaireExpose,
    });
  };
  const isFromExhibition = type === "exhibition";
  const previewConfig = isFromExhibition
    ? {
        attachments: data.multipleprint?.attachments ?? [],
        type: "multipleprint" as const,
        alt:
          data.multipleprint?.estampesMultiplesDescriptionNumeroEnregistrement ??
          data.multipleprint?.estampesMultiplesDescriptionTitre,
      }
    : {
        attachments: data.exhibition?.attachments ?? [],
        type: "exhibition" as const,
        alt:
          data.exhibition?.expositionsDescriptionNumeroExpo ??
          data.exhibition?.expositionsDescriptionTitre,
      };

  const sheetOpen = open ?? false;
  const { showItinerantSidebar, isItinerantContext, fieldsWithHint } =
    useItinerantDrawerChrome(itinerantConfig, fields);

  const catalogWatch = form.watch(
    "expositionsDansEstampesMultiplesDescriptionNumero",
  );
  const previewStops = useMemo(
    () =>
      stops.map((stop) =>
        stop.id === selectedStopId
          ? { ...stop, catalogNumber: catalogWatch ?? stop.catalogNumber }
          : stop,
      ),
    [stops, selectedStopId, catalogWatch],
  );

  return (
    <LinksDrawerLayout
      form={form}
      fields={fieldsWithHint}
      recordId={data.id}
      onSubmit={onSubmit}
      previewImage={previewConfig}
      open={open}
      onOpenChange={onOpenChange}
      submitDisabled={isSyncing}
      formFooter={
        isItinerantContext ? (
          <ItinerantExhibitionPreview stops={previewStops} />
        ) : undefined
      }
      sidebar={
        showItinerantSidebar && itinerantConfig ? (
          <ItinerantExhibitionPanel
            open={sheetOpen}
            seedExhibition={itinerantConfig.seedExhibition}
            initialStops={itinerantConfig.initialStops}
            groupId={itinerantConfig.groupId}
            parentType={itinerantConfig.parentType}
            parentId={itinerantConfig.parentId}
            stops={stops}
            onStopsChange={setStops}
            inlineFields={false}
            selectedStopId={selectedStopId}
            onSelectStop={selectStop}
          />
        ) : undefined
      }
    />
  );
};

export default MultipleprintInExhibitionDrawer;
